import { useCallback, useEffect, useState } from "react"
import { product_quick_view_path } from "@/lib/routes"
import type { QuickViewData } from "./types"

type Loaded = { key: string; data: QuickViewData | null; failed: boolean }

// Loads one product in one market. Results remember which product and market
// they answer, so a slow older answer is never shown for a newer pick.
// Nothing is fetched without a market. A reload of the same product keeps
// showing the last answer until the new one arrives.
export function useQuickView(externalId: string, marketSlug: string | null) {
  const [attempt, setAttempt] = useState(0)
  const product = marketSlug ? `${externalId}|${marketSlug}|` : ""
  const key = marketSlug ? `${product}${attempt}` : ""
  const [loaded, setLoaded] = useState<Loaded>({ key: "", data: null, failed: false })

  useEffect(() => {
    if (!marketSlug) return
    const controller = new AbortController()
    fetch(product_quick_view_path(externalId, { market: marketSlug }), {
      headers: { Accept: "application/json" },
      signal: controller.signal,
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((data: QuickViewData) => setLoaded({ key, data, failed: false }))
      .catch((e) => {
        if (e.name !== "AbortError") setLoaded({ key, data: null, failed: true })
      })
    return () => controller.abort()
  }, [externalId, marketSlug, key])

  const reload = useCallback(() => setAttempt((n) => n + 1), [])

  const current = marketSlug !== null && loaded.key === key
  const refreshing = !current && marketSlug !== null && loaded.data !== null && loaded.key.startsWith(product)
  return {
    data: current || refreshing ? loaded.data : null,
    loading: marketSlug !== null && !current && !refreshing,
    failed: current && loaded.failed,
    reload,
  }
}
